import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Plus, Settings, Instagram, Facebook, Youtube, Trash2, CheckCircle2, Heart, MessageCircle, Share2, Eye, BarChart3, TrendingUp, Zap, Bookmark, Search, X, Pencil, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SocialMediaCredentialsDialog } from "./SocialMediaCredentialsDialog";
import { AddPostByUrl } from "./AddPostByUrl";
import { logger } from "@/lib/logger";

interface SocialMediaAccount {
  id: string;
  platform: string;
  account_id: string;
  account_name: string;
  profile_url: string | null;
  avatar_url: string | null;
  connected_at: string;
  last_synced_at: string | null;
  is_active: boolean;
  sync_method: string;
}

interface SocialMediaPost {
  id: string;
  platform: string;
  post_id: string;
  account_name: string | null;
  content: string | null;
  image_url: string | null;
  post_url: string | null;
  posted_at: string;
  likes_count: number;
  comments_count: number;
  shares_count: number;
  views_count: number;
  impressions: number | null;
  reach: number | null;
  engagement: number | null;
  saves: number | null;
  is_added_to_report: boolean;
  account: {
    account_name: string;
    platform: string;
  };
}

interface SocialMediaManagerProps {
  northId: string;
  isPublished?: boolean;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  onUpdate?: () => void; // Callback to refresh parent component data
}

export const SocialMediaManager = ({ northId, isPublished = false, primaryColor, secondaryColor, onUpdate }: SocialMediaManagerProps) => {
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [accounts, setAccounts] = useState<SocialMediaAccount[]>([]);
  const [addedCount, setAddedCount] = useState(0);
  const [showOnReport, setShowOnReport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false); // Track background sync status
  const [searchQuery, setSearchQuery] = useState("");
  const [activePlatform, setActivePlatform] = useState<string>("added");
  const [displayCount, setDisplayCount] = useState(20);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreToSync, setHasMoreToSync] = useState(false);
  const observerTarget = useRef<HTMLTableRowElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State for editing account name
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [editingAccountName, setEditingAccountName] = useState("");
  
  // State for credentials dialog
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [useCustomCredentials, setUseCustomCredentials] = useState(false);
  
  // State for user domain check
  const [userEmail, setUserEmail] = useState<string>("");
  const [canUsePublerSync, setCanUsePublerSync] = useState(false);

  const fetchAddedCount = async () => {
    try {
      // Count via junction table (new posts: north_id = null on the post row)
      const { count: junctionCount, error: junctionError } = await supabase
        .from('north_social_posts')
        .select('*', { count: 'exact', head: true })
        .eq('north_id', northId);

      if (junctionError) throw junctionError;

      // Count direct posts (legacy: north_id set directly on social_media_posts)
      const { count: directCount, error: directError } = await supabase
        .from('social_media_posts')
        .select('*', { count: 'exact', head: true })
        .eq('north_id', northId)
        .eq('is_added_to_report', true);

      if (directError) throw directError;

      setAddedCount((junctionCount || 0) + (directCount || 0));
    } catch (error) {
      logger.error('Error fetching added count:', error);
    }
  };

  // Fetch posts from database only (no syncing)
  const fetchPostsFromDB = async (platform?: string, offset: number = 0) => {
    try {
      if (offset === 0) {
        setIsLoading(true);
        setPosts([]); // Clear posts when starting fresh
        setDisplayCount(20); // Reset display count
      } else {
        setIsLoadingMore(true);
      }
      
      logger.debug(`📂 Loading ${platform || 'all'} posts from database (offset: ${offset})...`);
      
      // First, get the user's team_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();
      
      if (!profile?.team_id) throw new Error('User has no team');
      
      // Query team pool posts (team_id set)
      let teamQuery = supabase
        .from('social_media_posts')
        .select(`
          *,
          account_name,
          account:social_media_accounts!left(account_name, platform, is_active)
        `, { count: 'exact' })
        .eq('team_id', profile.team_id)
        .order('posted_at', { ascending: false});
      
      // Filter by platform if specified
      if (platform) {
        teamQuery = teamQuery.eq('platform', platform);
      }
      
      // Apply range AFTER all filters - load 100 at a time
      teamQuery = teamQuery.range(offset, offset + 99);
      
      const { data: teamPostsData, error: teamPostsError, count } = await teamQuery;
      
      // Also query manual posts for this North (north_id set, but team_id also set for RLS)
      // Manual posts have both north_id and team_id set, but are filtered by north_id
      let manualQuery = supabase
        .from('social_media_posts')
        .select(`
          *,
          account_name,
          account:social_media_accounts!left(account_name, platform, is_active)
        `)
        .eq('north_id', northId)
        .not('north_id', 'is', null) // Ensure north_id is set (manual posts)
        .order('posted_at', { ascending: false});
      
      // Filter by platform if specified
      if (platform) {
        manualQuery = manualQuery.eq('platform', platform);
      }
      
      const { data: manualPostsData, error: manualPostsError } = await manualQuery;
      
      // Merge both queries, but exclude manual posts from team query (they have north_id set)
      // Team pool posts have north_id null, manual posts have north_id set
      const teamPoolPosts = (teamPostsData || []).filter(p => !p.north_id);
      const allPostsData = [
        ...teamPoolPosts,
        ...(manualPostsData || [])
      ].sort((a, b) => new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime());
      
      // Apply range to merged results
      const postsData = allPostsData.slice(offset, offset + 99);
      const postsError = teamPostsError || manualPostsError;
      
      if (postsError) {
        logger.error('❌ Error fetching posts from DB:', postsError);
        throw postsError;
      }
      
      // Check which posts are added to THIS North
      const postIds = postsData?.map(p => p.id) || [];
      const { data: addedPosts } = await supabase
        .from('north_social_posts')
        .select('social_post_id')
        .eq('north_id', northId)
        .in('social_post_id', postIds);
      
      const addedPostIds = new Set(addedPosts?.map(ap => ap.social_post_id) || []);
      
      // Mark posts as added if they're in the junction table
      const markedPosts = postsData?.map(post => ({
        ...post,
        is_added_to_report: addedPostIds.has(post.id)
      })) || [];
      
      logger.debug(`✅ Loaded ${markedPosts?.length || 0} ${platform || 'all'} posts from DB (total: ${count}, offset: ${offset})`);
      
      // If offset is 0, replace posts, otherwise append
      if (offset === 0) {
        setPosts(markedPosts || []);
        setDisplayCount(20); // Show first 20
      } else {
        setPosts(prev => {
          // Avoid duplicates by checking post IDs
          const existingIds = new Set(prev.map(p => p.id));
          const newPosts = (markedPosts || []).filter(p => !existingIds.has(p.id));
          return [...prev, ...newPosts];
        });
      }
      
      // Check if there are more posts to load
      const totalLoaded = offset === 0 ? (postsData?.length || 0) : posts.length + (postsData?.length || 0);
      setHasMoreToSync(count ? totalLoaded < count : false);
      
      logger.debug(`📊 Load status: ${totalLoaded} loaded, ${count} total, hasMore: ${count ? totalLoaded < count : false}`);
    } catch (error) {
      logger.error('Error loading posts from database:', error);
      toast({
        title: "Error",
        description: "Failed to load posts from database",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    checkUserDomain();
    fetchAccounts();
    fetchAddedCount(); // Always fetch the added count
    // Fetch posts for "added" tab on mount
    fetchAddedPosts();
    fetchUserCredentials();
    loadVisibilitySetting();
  }, [northId]);

  const checkUserDomain = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) return;
      
      setUserEmail(user.email);
      setCanUsePublerSync(user.email.endsWith('@caldwellrg.com'));
    } catch (error) {
      logger.error('Error checking user domain:', error);
    }
  };

  const loadVisibilitySetting = async () => {
    try {
      const { data, error } = await supabase
        .from('norths')
        .select('show_social_on_report')
        .eq('id', northId)
        .single();

      if (error) throw error;
      if (data?.show_social_on_report !== null && data?.show_social_on_report !== undefined) {
        setShowOnReport(data.show_social_on_report);
      }
    } catch (error) {
      logger.error('Error loading visibility setting:', error);
    }
  };

  const handleToggleVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from('norths')
        .update({ show_social_on_report: checked })
        .eq('id', northId);

      if (error) throw error;
      setShowOnReport(checked);
      
      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }
      
      toast({
        title: checked ? "Shown on report" : "Hidden from report",
        description: checked 
          ? "Social media posts will appear on the published report" 
          : "Social media posts are hidden from the published report",
      });
    } catch (error) {
      logger.error('Error updating visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update visibility setting",
        variant: "destructive",
      });
    }
  };
  
  const fetchUserCredentials = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('user_social_credentials')
        .select('use_custom_credentials')
        .eq('user_id', user.id)
        .maybeSingle();

      setUseCustomCredentials(data?.use_custom_credentials || false);
    } catch (error) {
      logger.error('Error fetching user credentials status:', error);
    }
  };

  useEffect(() => {
    // When switching tabs, just load from database (fast)
    if (activePlatform !== 'added') {
      fetchPostsFromDB(activePlatform);
    } else {
      // Refresh added posts when switching to "added" tab
      fetchAddedPosts();
    }
  }, [activePlatform]);

  const fetchAddedPosts = async () => {
    try {
      setIsLoading(true);
      logger.debug('🔍 Fetching added posts for northId:', northId);
      
      // Get posts added to THIS North via junction table (team pool posts)
      const { data: junctionData, error: junctionError } = await supabase
        .from('north_social_posts')
        .select(`
          social_post_id,
          social_media_posts:social_post_id (
            *,
            account:social_media_accounts!left(account_name, platform)
          )
        `)
        .eq('north_id', northId);
      
      // Also get manual posts directly associated with this North (north_id set, is_added_to_report true)
      const { data: manualPostsData, error: manualPostsError } = await supabase
        .from('social_media_posts')
        .select(`
          *,
          account:social_media_accounts!left(account_name, platform)
        `)
        .eq('north_id', northId)
        .eq('is_added_to_report', true)
        .not('north_id', 'is', null);
      
      if (junctionError) throw junctionError;
      if (manualPostsError) throw manualPostsError;
      
      // Flatten the nested structure from junction table
      const junctionPosts = junctionData?.map(item => ({
        ...item.social_media_posts,
        is_added_to_report: true // Mark as added for UI
      })) || [];
      
      // Manual posts are already flat
      const manualPosts = manualPostsData || [];
      
      // Combine and deduplicate by post id
      const allPostsMap = new Map();
      [...junctionPosts, ...manualPosts].forEach(post => {
        if (post && post.id) {
          allPostsMap.set(post.id, {
            ...post,
            is_added_to_report: true
          });
        }
      });
      
      // Sort by posted_at
      const sortedData = Array.from(allPostsMap.values()).sort((a: any, b: any) => 
        new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime()
      );
      
      logger.debug('✅ Added posts fetched:', sortedData?.length, 'posts');
      logger.debug(`   - ${junctionPosts.length} from junction table`);
      logger.debug(`   - ${manualPosts.length} manual posts`);
      logger.debug('Sample data:', sortedData?.slice(0, 2));
      
      setPosts(sortedData || []);
      
      // Also refresh the added count
      await fetchAddedCount();
    } catch (error) {
      logger.error('Error fetching added posts:', error);
      toast({
        title: "Error",
        description: "Failed to load posts",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      // Get user's team_id to fetch team-level accounts
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();
      
      if (!profile?.team_id) return;
      
      // Fetch accounts for this user (accounts are per user, but visible to team)
      const { data, error } = await supabase
        .from('social_media_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('connected_at', { ascending: false });

      if (error) throw error;
      
      setAccounts(data || []);
    } catch (error) {
      logger.error('Error fetching accounts:', error);
    }
  };

  const fetchPosts = async (cursor?: string, platform?: string) => {
    try {
      if (cursor) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setPosts([]); // Clear posts when starting fresh
        setNextCursor(null);
      }
      
      // Determine which platform to sync based on active tab
      const syncPlatform = platform || (activePlatform !== 'added' ? activePlatform : undefined);
      
      logger.debug(`🔄 Fetching posts for platform: ${syncPlatform || 'all'}`);
      
      // Use Publer for Instagram, Facebook, and YouTube
      const functionName = 'sync-publer';
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { 
          northId,
          limit: 100,
          after: cursor || undefined,
          platform: syncPlatform
        }
      });

      if (error) {
        logger.error('❌ Error calling sync function:', error);
        throw error;
      }
      
      // Refresh accounts list in case a new account was connected
      if (!cursor) {
        await fetchAccounts();
      }
      
      // Get team_id from North's owner's profile
      // Posts are stored in a team pool, not per North
      const { data: northData, error: northError } = await supabase
        .from('norths')
        .select('user_id')
        .eq('id', northId)
        .single();
      
      if (northError || !northData) {
        throw new Error('North not found');
      }
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', northData.user_id)
        .single();
      
      if (profileError || !profileData?.team_id) {
        throw new Error('Team not found for North owner');
      }
      
      const teamId = profileData.team_id;
      
      // After syncing, fetch posts from database with account information joined
      // Query by team_id (team pool) - this gets all team posts
      // Also include posts directly linked to this North (manual posts) and via junction table
      let query = supabase
        .from('social_media_posts')
        .select(`
          id,
          post_id,
          platform,
          content,
          image_url,
          post_url,
          posted_at,
          likes_count,
          comments_count,
          shares_count,
          views_count,
          impressions,
          reach,
          engagement,
          saves,
          is_added_to_report,
          account_id,
          team_id,
          north_id,
          account:social_media_accounts!left(account_name, platform)
        `)
        .eq('team_id', teamId)
        .order('posted_at', { ascending: false })
        .limit(100);
      
      // Filter by platform if specified
      if (syncPlatform) {
        query = query.eq('platform', syncPlatform);
      }
      
      const { data: postsData, error: postsError } = await query;
      
      // Also fetch posts directly associated with this North (manual URL posts)
      // These have north_id set and team_id null
      let manualPostsQuery = supabase
        .from('social_media_posts')
        .select(`
          id,
          post_id,
          platform,
          content,
          image_url,
          post_url,
          posted_at,
          likes_count,
          comments_count,
          shares_count,
          views_count,
          impressions,
          reach,
          engagement,
          saves,
          is_added_to_report,
          account_id,
          team_id,
          north_id,
          account:social_media_accounts!left(account_name, platform)
        `)
        .eq('north_id', northId)
        .is('team_id', null)
        .order('posted_at', { ascending: false })
        .limit(100);
      
      // Filter by platform if specified
      if (syncPlatform) {
        manualPostsQuery = manualPostsQuery.eq('platform', syncPlatform);
      }
      
      const { data: manualPostsData } = await manualPostsQuery;
      
      // Also fetch posts linked to this North via junction table (team pool posts added to this North)
      // This ensures we get all posts that should be visible for this North
      const { data: junctionPostsData } = await supabase
        .from('north_social_posts')
        .select(`
          social_post_id,
          social_media_posts:social_post_id (
            *,
            account:social_media_accounts!left(account_name, platform)
          )
        `)
        .eq('north_id', northId);
      
      // Merge posts from all three queries, deduplicating by post id
      const allPostsMap = new Map();
      
      // Add team pool posts (these are the main posts - Caldwell Group posts should be here)
      if (postsData) {
        logger.debug(`📦 Team pool query returned ${postsData.length} posts`);
        postsData.forEach(post => {
          // Preserve all fields including image_url
          allPostsMap.set(post.id, {
            ...post,
            // Ensure image_url is preserved
            image_url: post.image_url || null
          });
        });
      }
      
      // Add manual posts (directly associated with this North via north_id)
      if (manualPostsData) {
        logger.debug(`📝 Manual posts query returned ${manualPostsData.length} posts`);
        manualPostsData.forEach(post => {
          // Only add if not already in map (team pool takes precedence)
          if (!allPostsMap.has(post.id)) {
            allPostsMap.set(post.id, {
              ...post,
              // Ensure image_url is preserved
              image_url: post.image_url || null
            });
          }
        });
      }
      
      // Add junction table posts (flatten nested structure) - these are team pool posts added to this specific North
      if (junctionPostsData) {
        logger.debug(`🔗 Junction table query returned ${junctionPostsData.length} linked posts`);
        junctionPostsData.forEach(junction => {
          if (junction.social_media_posts) {
            const post = junction.social_media_posts;
            // Only add if not already in map (team pool takes precedence)
            if (!allPostsMap.has(post.id)) {
              allPostsMap.set(post.id, {
                ...post,
                // Ensure image_url is preserved
                image_url: post.image_url || null
              });
            }
          }
        });
      }
      
      // Convert map back to array and apply platform filter if needed
      let mergedPosts = Array.from(allPostsMap.values());
      
      if (syncPlatform) {
        mergedPosts = mergedPosts.filter(post => post.platform === syncPlatform);
      }
      
      // Sort by posted_at
      mergedPosts.sort((a, b) => 
        new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime()
      );
      
      // Limit to 100
      const finalPostsData = mergedPosts.slice(0, 100);
      
      if (postsError) {
        logger.error('❌ Error fetching posts from DB:', postsError);
        throw postsError;
      }
      
      // Check which posts are linked to this North via junction table
      const postIds = finalPostsData?.map(p => p.id) || [];
      let addedPostIds = new Set<string>();
      
      if (postIds.length > 0) {
        const { data: junctionData } = await supabase
          .from('north_social_posts')
          .select('social_post_id')
          .eq('north_id', northId)
          .in('social_post_id', postIds);
        
        if (junctionData) {
          addedPostIds = new Set(junctionData.map(j => j.social_post_id));
        }
      }
      
      // Mark posts as added if they're in the junction table
      const postsWithAddedStatus = finalPostsData?.map(post => ({
        ...post,
        is_added_to_report: addedPostIds.has(post.id)
      })) || [];
      
      logger.debug(`✅ Fetched ${postsWithAddedStatus.length} ${syncPlatform || 'all'} posts from DB`);
      logger.debug(`   - ${addedPostIds.size} posts are added to this North`);
      logger.debug('Sample posts:', postsWithAddedStatus?.slice(0, 2)); // Debug: log first 2 posts
      
      // Debug: Check image URLs and account info
      const postsWithImages = postsWithAddedStatus.filter(p => p.image_url);
      const postsWithoutImages = postsWithAddedStatus.filter(p => !p.image_url);
      const caldwellPosts = postsWithAddedStatus.filter(p => 
        p.account?.account_name === 'Caldwell Group' || p.account_name === 'Caldwell Group'
      );
      const caldwellPostsWithImages = caldwellPosts.filter(p => p.image_url);
      
      logger.debug(`📸 Image stats: ${postsWithImages.length} with images, ${postsWithoutImages.length} without images`);
      logger.debug(`🏢 Caldwell Group posts: ${caldwellPosts.length} total, ${caldwellPostsWithImages.length} with images`);
      
      // Log first 5 posts with their image URLs to debug
        logger.debug('First 5 posts image URLs:', postsWithAddedStatus.slice(0, 5).map(p => ({
        id: p.id,
        post_id: p.post_id,
        platform: p.platform,
        has_image: !!p.image_url,
        image_url: p.image_url ? p.image_url.substring(0, 150) + (p.image_url.length > 150 ? '...' : '') : null,
        account_name: p.account?.account_name || p.account_name
      })));
      
      if (postsWithoutImages.length > 0 && postsWithoutImages.length <= 5) {
        logger.debug('Posts without images:', postsWithoutImages.map(p => ({ 
          id: p.id, 
          post_id: p.post_id, 
          platform: p.platform,
          account_name: p.account?.account_name || p.account_name,
          image_url: p.image_url 
        })));
      }
      
      // Log sample Caldwell Group posts to debug
      if (caldwellPosts.length > 0) {
        logger.debug('Sample Caldwell Group posts:', caldwellPosts.slice(0, 3).map(p => ({
          id: p.id,
          post_id: p.post_id,
          platform: p.platform,
          has_image: !!p.image_url,
          image_url_preview: p.image_url ? p.image_url.substring(0, 150) + (p.image_url.length > 150 ? '...' : '') : null
        })));
      }
      
      if (postsWithAddedStatus) {
        setPosts(postsWithAddedStatus);
        setNextCursor(data?.nextCursor);
        setHasMoreToSync(data?.hasMore);
      }
      
      if (!cursor) {
        toast({
          title: "Success",
          description: data.message || "Posts synced successfully",
        });
      }
    } catch (error) {
      logger.error('Error fetching posts:', error);
      toast({
        title: "Error",
        description: "Failed to load social media posts",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleDisconnectAccount = async (accountId: string, accountName: string) => {
    try {
      const { error } = await supabase
        .from('social_media_accounts')
        .delete()
        .eq('id', accountId);

      if (error) throw error;

      toast({
        title: "Account disconnected",
        description: `${accountName} has been removed`
      });

      await fetchAccounts();
      
      // Refresh posts based on active tab
      if (activePlatform === 'added') {
        await fetchAddedPosts();
      } else {
        await fetchPosts();
      }
    } catch (error) {
      logger.error('Error disconnecting account:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect account",
        variant: "destructive"
      });
    }
  };

  const handleEditAccount = (accountId: string, currentName: string) => {
    setEditingAccountId(accountId);
    setEditingAccountName(currentName);
  };

  const handleSaveAccountName = async (accountId: string) => {
    if (!editingAccountName.trim()) {
      toast({
        title: "Error",
        description: "Account name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setIsRefreshing(true);
    try {
      // Get the account_id for this record to update all instances
      const { data: account } = await supabase
        .from('social_media_accounts')
        .select('account_id, platform')
        .eq('id', accountId)
        .single();

      if (!account) throw new Error('Account not found');

      // Update ALL accounts with the same account_id and platform
      // This ensures the name is synced across all Norths
      const { error } = await supabase
        .from('social_media_accounts')
        .update({ account_name: editingAccountName.trim() })
        .eq('account_id', account.account_id)
        .eq('platform', account.platform);

      if (error) throw error;

      toast({
        title: "Account updated",
        description: "Account name updated across all properties",
      });

      setEditingAccountId(null);
      setEditingAccountName("");
      
      // Refresh accounts
      await fetchAccounts();
      
      // Refresh posts based on active tab to update account names in the table
      if (activePlatform === 'added') {
        await fetchAddedPosts();
      } else {
        await fetchPosts();
      }
      
      // Invalidate React Query cache to update SocialCampaigns on the report
      queryClient.invalidateQueries({ queryKey: ['social-media-posts'] });
    } catch (error) {
      logger.error('Error updating account name:', error);
      toast({
        title: "Error",
        description: "Failed to update account name",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAccountId(null);
    setEditingAccountName("");
  };

  // Trigger sync in background (non-blocking)
  const triggerSync = async (platform?: string) => {
    try {
      setIsSyncing(true);
      const syncPlatform = platform || (activePlatform !== 'added' ? activePlatform : undefined);
      
      logger.debug(`🔄 Starting background sync for platform: ${syncPlatform || 'all'}`);
      
      // Invoke sync function but don't wait for it
      const { error } = await supabase.functions.invoke('sync-publer', {
        body: { 
          northId,
          limit: 100,
          platform: syncPlatform
        }
      });

      if (error) {
        logger.error('❌ Error triggering sync:', error);
        toast({
          title: "Sync Error",
          description: "Failed to start sync. Posts may be outdated.",
          variant: "destructive"
        });
      } else {
        logger.debug('✅ Sync started in background');
        // Wait a bit for sync to process, then refresh posts
        setTimeout(async () => {
          // Refresh posts from DB after sync has had time to process
          if (activePlatform === 'added') {
            await fetchAddedPosts();
          } else {
            await fetchPostsFromDB(activePlatform !== 'added' ? activePlatform : undefined);
          }
          await fetchAccounts(); // Refresh accounts in case new ones were added
          await fetchAddedCount(); // Refresh count
        }, 3000); // Wait 3 seconds for sync to process some posts
      }
    } catch (error) {
      logger.error('Error triggering sync:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      setNextCursor(null);
      setHasMoreToSync(false);
      
      // First, immediately load existing posts from DB (fast)
      await fetchAccounts();
      await fetchAddedCount();
      
      if (activePlatform === 'added') {
        await fetchAddedPosts();
      } else {
        // Load posts from DB immediately
        await fetchPostsFromDB(activePlatform !== 'added' ? activePlatform : undefined);
      }
      
      // Then trigger sync in background (non-blocking)
      // Don't await - let it run while user can see posts
      triggerSync(activePlatform !== 'added' ? activePlatform : undefined);
      
      toast({
        title: "Refreshing",
        description: "Posts loaded. Syncing in background...",
      });
    } catch (error) {
      logger.error('Error refreshing:', error);
      toast({
        title: "Error",
        description: "Failed to load posts",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLoadMore = async () => {
    if (hasMoreToSync && !isLoadingMore) {
      const currentOffset = posts.length;
      await fetchPostsFromDB(activePlatform !== 'added' ? activePlatform : undefined, currentOffset);
    }
  };

  const togglePostInReport = async (postId: string, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        // Remove from junction table
        const { error } = await supabase
          .from('north_social_posts')
          .delete()
          .eq('north_id', northId)
          .eq('social_post_id', postId);

        if (error) throw error;
      } else {
        // Add to junction table
        const { data: { user } } = await supabase.auth.getUser();
        const { error } = await supabase
          .from('north_social_posts')
          .insert({
            north_id: northId,
            social_post_id: postId,
            added_by: user?.id
          });

        if (error) throw error;
      }

      // Update local state
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, is_added_to_report: !currentStatus }
          : post
      ));

      // Refresh the added count badge
      await fetchAddedCount();

      // Invalidate React Query cache to refresh SocialCampaigns component
      queryClient.invalidateQueries({ queryKey: ['social-campaigns', northId] });
      queryClient.invalidateQueries({ queryKey: ['social-campaigns'] });

      toast({
        title: currentStatus ? "Removed from report" : "Added to report",
        description: currentStatus 
          ? "Post removed from seller report" 
          : "Post will appear in the seller report"
      });
    } catch (error) {
      logger.error('Error toggling post:', error);
      toast({
        title: "Error",
        description: "Failed to update post",
        variant: "destructive"
      });
    }
  };

  const handleDeleteManualPost = async (postId: string) => {
    try {
      const { error } = await supabase
        .from('social_media_posts')
        .delete()
        .eq('id', postId)
        .is('account_id', null); // Extra safety: only delete manual posts

      if (error) throw error;

      // Remove from local state
      setPosts(posts.filter(post => post.id !== postId));

      // Refresh the added count badge
      await fetchAddedCount();

      // Invalidate React Query cache to refresh SocialCampaigns component
      queryClient.invalidateQueries({ queryKey: ['social-campaigns', northId] });
      queryClient.invalidateQueries({ queryKey: ['social-campaigns'] });

      toast({
        title: "Post deleted",
        description: "Manually added post has been removed"
      });
    } catch (error) {
      logger.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive"
      });
    }
  };

  const filteredPosts = posts.filter(post => {
    // For "added" tab, only show posts that are added to report
    if (activePlatform === 'added') {
      const matchesSearch = !searchQuery || 
        post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.account?.account_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.account_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return post.is_added_to_report && matchesSearch;
    }
    
    // For platform tabs, only show posts with accounts
    if (!post.account) return false;
    
    // For platform tabs, show all posts from that platform
    const matchesPlatform = post.platform === activePlatform;
    const matchesSearch = !searchQuery || 
      post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.account?.account_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.account_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesPlatform && matchesSearch;
  });
  
  // Infinite scroll - display first N posts
  const displayedPosts = filteredPosts.slice(0, displayCount);
  const hasMore = displayCount < filteredPosts.length;
  
  // Reset display count when changing filters
  useEffect(() => {
    setDisplayCount(20);
  }, [activePlatform, searchQuery]);
  
  // Optional: Intersection Observer for automatic infinite scroll (disabled for now, using button instead)
  // Uncomment this if you want automatic scrolling back
  /*
  useEffect(() => {
    const currentHasMore = displayCount < filteredPosts.length;
    if (!currentHasMore && !hasMoreToSync) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && !isLoading && !isLoadingMore) {
          const currentDisplayCount = displayCount;
          const currentFilteredLength = filteredPosts.length;
          const currentHasMoreLocal = currentDisplayCount < currentFilteredLength;
          
          if (currentHasMoreLocal) {
            setDisplayCount(prev => Math.min(prev + 20, currentFilteredLength));
          } else if (!currentHasMoreLocal && hasMoreToSync) {
            handleLoadMore();
          }
        }
      },
      { threshold: 0.1, root: null, rootMargin: '500px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [displayCount, filteredPosts.length, hasMoreToSync, isLoading, isLoadingMore, posts.length]);
  */

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram': return <Instagram className="h-5 w-5" />;
      case 'facebook': return <Facebook className="h-5 w-5" />;
      case 'youtube': return <Youtube className="h-5 w-5" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="space-y-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">Social Media</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {canUsePublerSync
              ? "View and manage your social media posts from Instagram, Facebook, and YouTube."
              : "Add social media posts by URL to show the seller how you're promoting their listing."}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-on-report"
              checked={showOnReport}
              onCheckedChange={handleToggleVisibility}
              disabled={isPublished}
            />
            <Label htmlFor="show-on-report" className="text-sm font-medium cursor-pointer whitespace-nowrap">
              Show on Report
            </Label>
          </div>
          <div className="flex gap-2">
            {canUsePublerSync && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleRefresh}
                  disabled={isRefreshing || isPublished}
                  variant="default"
                  className="gap-2 w-full sm:w-auto"
                  style={primaryColor ? { backgroundColor: primaryColor, color: getContrastColor(primaryColor) } : undefined}
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing || isSyncing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Loading...' : isSyncing ? 'Syncing...' : 'Sync Posts'}
                </Button>
                {isSyncing && !isRefreshing && (
                  <span className="text-xs text-muted-foreground">
                    Syncing in background...
                  </span>
                )}
              </div>
            )}
            <AddPostByUrl
              northId={northId}
              onPostAdded={fetchAddedPosts}
              secondaryColor={secondaryColor}
            />
          </div>
        </div>
      </div>

      {/* Connected Accounts */}
      {canUsePublerSync && accounts.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Connected Accounts</h3>
            <div className="flex flex-wrap gap-4">
              {accounts.map((account) => (
                <div 
                  key={account.id} 
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors flex-1 min-w-[300px]"
                >
                  <div className="flex items-center gap-3 flex-1">
                    {getPlatformIcon(account.platform)}
                    <div className="flex-1">
                      {editingAccountId === account.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editingAccountName}
                            onChange={(e) => setEditingAccountName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveAccountName(account.id);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            className="h-8"
                            autoFocus
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleSaveAccountName(account.id)}
                            disabled={isRefreshing}
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={handleCancelEdit}
                            disabled={isRefreshing}
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <p className="font-medium">{account.account_name}</p>
                          <p className="text-sm text-muted-foreground">
                            Connected via {account.sync_method === 'publer' ? 'Publer' : 'Meta Graph API'}
                            {account.last_synced_at && (
                              <span className="ml-2">
                                • Last synced {new Date(account.last_synced_at).toLocaleDateString()}
                              </span>
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {account.is_active && (
                      <Badge variant="secondary" className="gap-1 bg-green-600 text-white hover:bg-green-700">
                        <CheckCircle2 className="h-3 w-3" />
                        Active
                      </Badge>
                    )}
                    {editingAccountId !== account.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditAccount(account.id, account.account_name)}
                        disabled={isPublished}
                        title="Edit account name"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDisconnectAccount(account.id, account.account_name)}
                      disabled={isPublished || editingAccountId === account.id}
                      title="Disconnect account"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Platform Tabs & Search */}
      <div className="flex items-center gap-4 flex-wrap">
        <Tabs value={activePlatform} onValueChange={setActivePlatform} className="flex-1">
          <TabsList>
            <TabsTrigger value="added" className="gap-2">
              Added
              <Badge 
                variant="secondary" 
                className="ml-1"
                style={secondaryColor ? { backgroundColor: secondaryColor, color: getContrastColor(secondaryColor) } : undefined}
              >
                {addedCount}
              </Badge>
            </TabsTrigger>
            {canUsePublerSync && (
              <>
                <TabsTrigger value="instagram" className="gap-2">
                  <Instagram className="h-4 w-4" />
                  Instagram
                </TabsTrigger>
                <TabsTrigger value="facebook" className="gap-2">
                  <Facebook className="h-4 w-4" />
                  Facebook
                </TabsTrigger>
                <TabsTrigger value="youtube" className="gap-2">
                  <Youtube className="h-4 w-4" />
                  YouTube
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          <Input
            placeholder={
              activePlatform === 'added' 
                ? 'Search Added Posts...' 
                : `Search ${activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1)} Posts...`
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-80"
          />
          {canUsePublerSync && (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCredentialsDialogOpen(true)}
                title="Manage social media credentials"
                disabled={isPublished}
              >
                <Settings className="h-4 w-4" />
              </Button>
              {useCustomCredentials && (
                <Badge variant="default" className="text-xs">
                  Custom
                </Badge>
              )}
            </>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/30 sticky top-0 bg-card z-10">
                  <tr>
                    <th className="text-left p-4 font-medium w-24">Image</th>
                    <th className="text-left p-4 font-medium">Description</th>
                    <th className="text-left p-4 font-medium w-32">Date Posted</th>
                    <th className="text-left p-4 font-medium w-48">Account</th>
                    <th className="text-left p-4 font-medium w-40">Actions</th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Scrollable tbody */}
            <div className="overflow-y-auto max-h-[600px]">
              <table className="w-full">
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center p-8 text-muted-foreground">
                      Loading posts...
                    </td>
                  </tr>
                ) : filteredPosts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center p-8 text-muted-foreground">
                      {activePlatform === 'added' 
                        ? canUsePublerSync 
                          ? 'No posts added to report yet. Switch to a platform tab to browse and add posts.' 
                          : 'No posts added yet. Click "Add Post by URL" to add social media posts from Instagram, Facebook, or YouTube.'
                        : 'No posts found. Connect your social media accounts to get started.'}
                    </td>
                  </tr>
                ) : (
                  <>
                    {displayedPosts.map((post) => (
                      <tr key={post.id} className="border-b hover:bg-muted/20 transition-colors">
                        <td className="p-4 w-24">
                          {post.image_url ? (
                            <img 
                              src={post.image_url} 
                              alt="Post" 
                              className="w-16 h-16 object-cover rounded"
                              onError={(e) => {
                                logger.error(`❌ Failed to load image for post ${post.id} (${post.platform}):`, post.image_url);
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                if (fallback) fallback.classList.remove('hidden');
                              }}
                              onLoad={() => {
                                logger.debug(`✅ Successfully loaded image for post ${post.id} (${post.platform})`);
                              }}
                            />
                          ) : null}
                          <div className={`w-16 h-16 bg-muted rounded items-center justify-center ${post.image_url ? 'hidden' : 'flex'}`}>
                            {getPlatformIcon(post.platform)}
                          </div>
                        </td>
                        <td className="p-4 max-w-md">
                          <p className="line-clamp-2 text-sm">
                            {post.content?.replace(/^@\w+:\s*/, '') || post.post_url || 'No description'}
                          </p>
                          <div className="flex gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                            <span title="Likes" className="flex items-center gap-1">
                              <Heart className="h-3 w-3" /> {post.likes_count || 0}
                            </span>
                            <span title="Comments" className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" /> {post.comments_count || 0}
                            </span>
                            <span title="Shares" className="flex items-center gap-1">
                              <Share2 className="h-3 w-3" /> {post.shares_count || 0}
                            </span>
                            <span title="Views" className="flex items-center gap-1">
                              <Eye className="h-3 w-3" /> {post.views_count || 0}
                            </span>
                            <span title="Impressions" className="flex items-center gap-1">
                              <BarChart3 className="h-3 w-3" /> {post.impressions?.toLocaleString() || 0}
                            </span>
                            <span title="Reach" className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" /> {post.reach?.toLocaleString() || 0}
                            </span>
                            <span title="Engagement" className="flex items-center gap-1">
                              <Zap className="h-3 w-3" /> {post.engagement?.toLocaleString() || 0}
                            </span>
                            <span title="Saves" className="flex items-center gap-1">
                              <Bookmark className="h-3 w-3" /> {post.saves?.toLocaleString() || 0}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground w-32">
                          {new Date(post.posted_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 w-48">
                          <div className="flex items-center gap-2">
                            {getPlatformIcon(post.platform)}
                            <span className="text-sm">
                              {post.account?.account_name || 
                               post.account_name ||
                               (() => {
                                 const match = post.content?.match(/^([^:]+):/);
                                 return match ? match[1].trim().replace(/^@/, '') : 'Unknown';
                               })()}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 w-40">
                          <div className="flex items-center gap-2">
                            {!post.account ? (
                              // Manual posts: show delete button
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteManualPost(post.id)}
                                className="gap-2"
                                disabled={isPublished}
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </Button>
                            ) : (
                              // Synced posts: show add/remove button
                              <Button
                                variant={post.is_added_to_report ? "default" : "outline"}
                                size="sm"
                                onClick={() => togglePostInReport(post.id, post.is_added_to_report)}
                                className="gap-2"
                                disabled={isPublished}
                              >
                                {post.is_added_to_report ? (
                                  <>
                                    <span className="text-green-500">✓</span>
                                    Added
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-4 w-4" />
                                    Add
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Load More Button */}
                    {(hasMore || hasMoreToSync) && (
                      <tr>
                        <td colSpan={5} className="text-center p-8">
                          <Button
                            onClick={() => {
                              if (hasMore) {
                                setDisplayCount(prev => Math.min(prev + 20, filteredPosts.length));
                              } else if (hasMoreToSync) {
                                handleLoadMore();
                              }
                            }}
                            disabled={isLoadingMore || isLoading}
                            variant="outline"
                            className="gap-2"
                          >
                            {isLoadingMore ? (
                              <>
                                <RefreshCw className="h-4 w-4 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4" />
                                {hasMore 
                                  ? `Load More (Showing ${displayCount} of ${filteredPosts.length})`
                                  : 'Load More Posts'
                                }
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                     )}
                     {!hasMore && !hasMoreToSync && displayedPosts.length > 0 && (
                       <tr>
                         <td colSpan={5} className="text-center p-4 text-muted-foreground text-sm">
                           All {filteredPosts.length} posts loaded
                         </td>
                       </tr>
                     )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <div className="text-center py-12 bg-muted/20 rounded-lg">
            <p className="text-muted-foreground">Loading posts...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12 bg-muted/20 rounded-lg">
            <p className="text-muted-foreground mb-2">
              {activePlatform === 'added'
                ? canUsePublerSync
                  ? 'No posts added to report yet'
                  : 'No posts added yet'
                : 'No posts found'}
            </p>
            <p className="text-sm text-muted-foreground">
              {activePlatform === 'added'
                ? canUsePublerSync
                  ? 'Switch to a platform tab to browse and add posts'
                  : 'Click "Add Post by URL" to get started'
                : 'Connect your social media accounts to get started'}
            </p>
          </div>
        ) : (
          <>
            {displayedPosts.map((post) => (
              <div key={post.id} className="border rounded-lg p-3 bg-card">
                {/* Post Image and Header */}
                <div className="flex gap-3 mb-3">
                  {post.image_url ? (
                    <img
                      src={post.image_url}
                      alt="Post"
                      className="w-20 h-20 object-cover rounded flex-shrink-0"
                      onError={(e) => {
                        logger.error(`❌ Failed to load image for post ${post.id} (${post.platform}):`, post.image_url);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={() => {
                        logger.debug(`✅ Successfully loaded image for post ${post.id} (${post.platform})`);
                      }}
                    />
                  ) : (
                    <div className="w-20 h-20 bg-muted rounded flex items-center justify-center flex-shrink-0">
                      {getPlatformIcon(post.platform)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getPlatformIcon(post.platform)}
                      <span className="text-xs font-medium truncate">
                        {post.account?.account_name ||
                         post.account_name ||
                         (() => {
                           const match = post.content?.match(/^([^:]+):/);
                           return match ? match[1].trim().replace(/^@/, '') : 'Unknown';
                         })()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(post.posted_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Post Content */}
                <p className="text-sm mb-3 line-clamp-3">
                  {post.content?.replace(/^@\w+:\s*/, '') || post.post_url || 'No description'}
                </p>

                {/* Post Stats */}
                <div className="grid grid-cols-4 gap-2 mb-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Heart className="h-3 w-3" />
                    <span>{post.likes_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" />
                    <span>{post.comments_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Share2 className="h-3 w-3" />
                    <span>{post.shares_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    <span>{post.views_count || 0}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between gap-2 pt-3 border-t">
                  {!post.account ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteManualPost(post.id)}
                      className="h-8 px-3 text-xs"
                      disabled={isPublished}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </Button>
                  ) : (
                    <Button
                      variant={post.is_added_to_report ? "default" : "outline"}
                      size="sm"
                      onClick={() => togglePostInReport(post.id, post.is_added_to_report)}
                      className="h-8 px-3 text-xs"
                      disabled={isPublished}
                    >
                      {post.is_added_to_report ? (
                        <>
                          <span className="text-green-500 mr-1">✓</span>
                          Add to Report
                        </>
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Add to Report
                        </>
                      )}
                    </Button>
                  )}
                  <Badge
                    variant={post.is_added_to_report ? 'default' : 'secondary'}
                    className={`text-[10px] h-5 px-2 flex-shrink-0 ${post.is_added_to_report ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    {post.is_added_to_report ? 'Added' : 'Not Added'}
                  </Badge>
                </div>
              </div>
            ))}

            {/* Load More Button for mobile */}
            {(hasMore || hasMoreToSync) && (
              <div className="text-center py-6">
                <Button
                  onClick={() => {
                    if (hasMore) {
                      setDisplayCount(prev => Math.min(prev + 20, filteredPosts.length));
                    } else if (hasMoreToSync) {
                      handleLoadMore();
                    }
                  }}
                  disabled={isLoadingMore || isLoading}
                  variant="outline"
                  className="gap-2"
                >
                  {isLoadingMore ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      {hasMore 
                        ? `Load More (${displayCount} of ${filteredPosts.length})`
                        : 'Load More Posts'
                      }
                    </>
                  )}
                </Button>
              </div>
            )}

            {!hasMore && !hasMoreToSync && displayedPosts.length > 0 && (
              <div className="text-center py-4 text-muted-foreground text-sm">
                All {filteredPosts.length} posts loaded
              </div>
            )}
          </>
        )}
      </div>

      {/* Credentials Dialog */}
      {canUsePublerSync && (
        <SocialMediaCredentialsDialog
          open={credentialsDialogOpen}
          onOpenChange={setCredentialsDialogOpen}
          onCredentialsUpdated={async () => {
            await fetchUserCredentials();
            await handleRefresh();
          }}
        />
      )}
    </div>
  );
};
